/**
 * usePolling — hook reutilizable para polling pausable.
 *
 * Comportamiento:
 *   - Llama a `fn` al montar (tras `initialDelay` ms) y luego cada `intervalMs`.
 *   - PAUSA automáticamente cuando la pestaña pasa a background (document.hidden)
 *     y REANUDA cuando vuelve a primer plano, ejecutando un fetch inmediato.
 *   - Cancela si el componente se desmonta o si `enabled` cambia a false.
 *
 * Reduce drásticamente el coste de red cuando el usuario tiene varios tabs
 * o ha dejado el CRM en segundo plano (caso real reportado por usuario).
 *
 * @example
 *   usePolling(fetchData, 60_000, { enabled: !!user, initialDelay: 1000 });
 */
import { useEffect, useRef } from 'react';

export function usePolling(fn, intervalMs, options = {}) {
  const { enabled = true, initialDelay = 0 } = options;
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let timerId = null;
    let initialTimerId = null;

    const safeCall = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        await fnRef.current();
      } catch (e) {
        // Errores ya manejados por la función del consumer; aquí los swallow.
        console.debug('[usePolling] fn error:', e?.message || e);
      }
    };

    const startInterval = () => {
      if (timerId) return;
      timerId = setInterval(safeCall, intervalMs);
    };
    const stopInterval = () => {
      if (timerId) {
        clearInterval(timerId);
        timerId = null;
      }
    };

    const onVisibility = () => {
      if (cancelled) return;
      if (document.hidden) {
        stopInterval();
      } else {
        // Al volver al tab, hacer un fetch inmediato + reanudar timer
        safeCall();
        startInterval();
      }
    };

    initialTimerId = setTimeout(() => {
      if (cancelled) return;
      safeCall();
      startInterval();
    }, initialDelay);

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      cancelled = true;
      if (initialTimerId) clearTimeout(initialTimerId);
      stopInterval();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [enabled, intervalMs]);
}

export default usePolling;
